import { useState, useEffect, useCallback } from 'react';
import { thingsboardService } from '../services/thingsboard';

export interface TimeseriesData {
  [key: string]: {
    ts: number;
    value: string;
  }[];
}

export type TimeRange = 'realtime' | 'daily' | 'weekly' | 'monthly';

/**
 * Hook to fetch historical telemetry data from ThingsBoard
 */
export function useTelemetryHistory(
  targetId: string | null, 
  targetKeys: string[], 
  targetRange: TimeRange, 
  targetType: 'DEVICE' | 'ASSET' = 'DEVICE',
  aggMethod?: 'NONE' | 'AVG' | 'MIN' | 'MAX' | 'SUM'
) {
  const [data, setData] = useState<TimeseriesData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchHistoricalData = useCallback(async () => {
    if (!targetId /*|| targetRange === 'realtime'*/) {
      // if (targetRange === 'realtime') setData({}); // realtime is handled by useTelemetry
      return;
    }

    setLoading(true);
    try {
      let now = Date.now();
      let startTs = now;
      let interval = 0;
      let limit = 100;
      let agg: 'NONE' | 'AVG' | 'MIN' | 'MAX' | 'SUM' = 'NONE';

      // Auto-detect default aggregation if not explicitly provided
      const defaultAgg = targetKeys.some(k => k.includes('power') || k.includes('energy')) ? 'MAX' : 'AVG';

      switch (targetRange) {
        case 'realtime':
          startTs = now - 30 * 60 * 1000; // 30 minutes
          interval = 0;
          limit = 1000;
          agg = aggMethod || 'NONE';
          break;
        case 'daily': {
          // Last 24 hours, grouped by hour (3600000 ms)
          const todayObj = new Date(now);
          todayObj.setHours(todayObj.getHours() + 1, 0, 0, 0); // End of current hour
          const endOfHour = todayObj.getTime();
          startTs = endOfHour - 24 * 60 * 60 * 1000;
          now = endOfHour;
          interval = 3600000;
          limit = 24;
          agg = aggMethod || defaultAgg;
          break;
        }
        case 'weekly': {
          // Last 7 days, grouped by day (86400000 ms)
          const todayObj = new Date(now);
          todayObj.setHours(24, 0, 0, 0); // Midnight tonight / start of tomorrow
          const endOfDay = todayObj.getTime();
          startTs = endOfDay - 7 * 24 * 60 * 60 * 1000;
          now = endOfDay;
          interval = 86400000;
          limit = 7;
          agg = aggMethod || defaultAgg;
          break;
        }
        case 'monthly': {
          // Last 30 days, grouped by day
          const todayObj = new Date(now);
          todayObj.setHours(24, 0, 0, 0); // Midnight tonight / start of tomorrow
          const endOfDay = todayObj.getTime();
          startTs = endOfDay - 30 * 24 * 60 * 60 * 1000;
          now = endOfDay;
          interval = 86400000;
          limit = 30;
          agg = aggMethod || defaultAgg;
          break;
        }
      }

      const result = await thingsboardService.getTimeseries(
        targetType,
        targetId,
        targetKeys.join(','),
        startTs,
        now,
        interval,
        limit,
        agg
      );
      setData(result);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch historical telemetry', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType, targetKeys.join(','), targetRange]);

  useEffect(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  return { data, loading, error, refetch: fetchHistoricalData };
}
